import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Star, Crown, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MembershipBenefitsShowcaseProps {
  isOpen: boolean;
  onClose: () => void;
}

const planBenefits = {
  bronce: {
    name: 'Bronce',
    icon: Shield,
    gradient: 'from-amber-700 to-amber-900',
    accentColor: 'amber-600',
    borderGlow: 'shadow-[0_0_30px_rgba(180,83,9,0.3)]',
    benefits: [
      {
        title: '1 Vidrio Temperado',
        description: 'Protección básica para tu pantalla',
        image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=300&h=200&fit=crop',
        icon: '🛡️'
      },
      {
        title: '1 Estuche Space',
        description: 'Diseño moderno y resistente',
        image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=300&h=200&fit=crop',
        icon: '📱'
      },
      {
        title: '2 Limpiezas Mensuales',
        description: 'Mantené tu celular impecable',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=300&h=200&fit=crop',
        icon: '✨'
      },
      {
        title: 'Garantía 24 Horas',
        description: 'Si se daña, lo reemplazamos',
        image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=300&h=200&fit=crop',
        icon: '⏰'
      }
    ]
  },
  plata: {
    name: 'Plata',
    icon: Star,
    gradient: 'from-slate-400 to-slate-600',
    accentColor: 'slate-400',
    borderGlow: 'shadow-[0_0_30px_rgba(148,163,184,0.3)]',
    benefits: [
      {
        title: '2 Vidrios o 2 Hidrogel Mate',
        description: 'Elegí la protección que prefieras',
        image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=300&h=200&fit=crop',
        icon: '🛡️'
      },
      {
        title: '2 Estuches Space',
        description: 'Variedad de estilos para vos',
        image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=300&h=200&fit=crop',
        icon: '📱'
      },
      {
        title: '2 Limpiezas Mensuales',
        description: 'Limpieza profesional incluida',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=300&h=200&fit=crop',
        icon: '✨'
      },
      {
        title: 'Garantía 3 Días',
        description: 'Más tiempo de protección',
        image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=300&h=200&fit=crop',
        icon: '⏰'
      },
      {
        title: 'Atención Preferencial',
        description: 'Sin filas, servicio rápido',
        image: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=300&h=200&fit=crop',
        icon: '⭐'
      },
      {
        title: 'Envío Gratis +₡40.000',
        description: 'En compras mayores, te lo llevamos',
        image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=300&h=200&fit=crop',
        icon: '🚚'
      }
    ]
  },
  oro: {
    name: 'Oro',
    icon: Crown,
    gradient: 'from-yellow-400 to-amber-500',
    accentColor: 'yellow-500',
    borderGlow: 'shadow-[0_0_40px_rgba(251,191,36,0.4)]',
    benefits: [
      {
        title: '2 Temperados o 2 Hidrogel Premium',
        description: 'La mejor protección del mercado',
        image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=300&h=200&fit=crop',
        icon: '💎'
      },
      {
        title: '1 Estuche MagSafe/Premium',
        description: 'Calidad superior garantizada',
        image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=300&h=200&fit=crop',
        icon: '📱'
      },
      {
        title: '3 Limpiezas Mensuales',
        description: 'El máximo cuidado para tu dispositivo',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=300&h=200&fit=crop',
        icon: '✨'
      },
      {
        title: 'Garantía 5 Días',
        description: 'La garantía más extensa',
        image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=300&h=200&fit=crop',
        icon: '⏰'
      },
      {
        title: 'WhatsApp VIP Directo',
        description: 'Atención personalizada 24/7',
        image: 'https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=300&h=200&fit=crop',
        icon: '💬'
      },
      {
        title: 'Envío Gratis +₡20.000',
        description: 'Comprás y te lo llevamos',
        image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=300&h=200&fit=crop',
        icon: '🚚'
      }
    ]
  }
};

export const MembershipBenefitsShowcase: React.FC<MembershipBenefitsShowcaseProps> = ({ isOpen, onClose }) => {
  const [selectedPlan, setSelectedPlan] = useState<'bronce' | 'plata' | 'oro'>('oro');
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  const planOrder: Array<'bronce' | 'plata' | 'oro'> = ['oro', 'bronce', 'plata'];

  useEffect(() => {
    if (!isAutoRotating || !isOpen) return;

    const interval = setInterval(() => {
      setSelectedPlan((current) => {
        const currentIndex = planOrder.indexOf(current);
        const nextIndex = (currentIndex + 1) % planOrder.length;
        return planOrder[nextIndex];
      });
    }, 8000);

    return () => clearInterval(interval);
  }, [isAutoRotating, isOpen]);

  const handleManualSelect = (planKey: 'bronce' | 'plata' | 'oro') => {
    setSelectedPlan(planKey);
    setIsAutoRotating(false);
    // Resume auto-rotation after 15 seconds of no interaction
    setTimeout(() => setIsAutoRotating(true), 15000);
  };

  const plan = planBenefits[selectedPlan];
  const Icon = plan.icon;

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="w-full overflow-hidden"
    >
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-700/50 rounded-3xl p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-yellow-400" />
              ¿Qué incluye cada membresía?
            </h3>
            <p className="text-zinc-400 mt-1">Todo lo que recibís cada mes con tu plan</p>
          </div>
          
          {/* Plan selector - responsive grid for mobile */}
          <div className="grid grid-cols-3 gap-2 w-full md:w-auto md:flex">
            {(Object.keys(planBenefits) as Array<keyof typeof planBenefits>).map((planKey) => {
              const p = planBenefits[planKey];
              const PlanIcon = p.icon;
              const isSelected = selectedPlan === planKey;
              return (
                <button
                  key={planKey}
                  onClick={() => handleManualSelect(planKey)}
                  className={`px-3 md:px-5 py-3 rounded-xl flex items-center justify-center gap-1.5 md:gap-2 transition-all duration-300 ${
                    isSelected
                      ? `bg-gradient-to-r ${p.gradient} text-black font-bold ${p.borderGlow}`
                      : 'bg-zinc-800/80 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                  }`}
                >
                  <PlanIcon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="font-medium text-sm md:text-base">{p.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Benefits Grid - Neumorphic Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedPlan}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {plan.benefits.map((benefit, idx) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="group relative"
              >
                {/* Neumorphic Card */}
                <div className="relative bg-zinc-800/40 rounded-2xl overflow-hidden
                  shadow-[8px_8px_16px_rgba(0,0,0,0.4),-4px_-4px_12px_rgba(255,255,255,0.05)]
                  border border-zinc-700/30
                  hover:shadow-[12px_12px_24px_rgba(0,0,0,0.5),-6px_-6px_16px_rgba(255,255,255,0.08)]
                  transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Image */}
                  <div className="relative h-36 overflow-hidden">
                    <img 
                      src={benefit.image} 
                      alt={benefit.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent`} />
                    
                    {/* Icon Badge */}
                    <div className={`absolute top-3 right-3 w-12 h-12 rounded-xl bg-gradient-to-br ${plan.gradient} 
                      flex items-center justify-center text-2xl
                      shadow-[4px_4px_8px_rgba(0,0,0,0.3),-2px_-2px_6px_rgba(255,255,255,0.1)]`}
                    >
                      {benefit.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${plan.gradient} flex items-center justify-center flex-shrink-0 mt-0.5
                        shadow-[2px_2px_4px_rgba(0,0,0,0.3),-1px_-1px_3px_rgba(255,255,255,0.1)]`}
                      >
                        <Check className="w-4 h-4 text-black" />
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-lg mb-1">{benefit.title}</h4>
                        <p className="text-zinc-400 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Glow effect on hover */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-br ${plan.gradient} pointer-events-none`} />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
